import React from 'react';

import { Modal } from 'ui/modal/modal.ui';

import { Content } from './modal-adhesion-contract.styles';

interface ModalAdhesionContractProps {
  show: boolean;
  toggle: () => void;
}

/**
 * Contrato de Adesão — texto-base extraído do documento oficial
 * "Termo de Adesão Plataforma - V.03.docx" (Contratos/Solstício Energia).
 * O aceite é registrado em users.accepted_adhesion_at.
 *
 * ⚠ ADAPTAÇÃO: o documento original é preenchido por contrato individual
 * (plano, valor, forma de pagamento, assinatura e testemunhas específicos de
 * cada cliente Enterprise). Para o checkbox de aceite no cadastro self-service
 * — usado por qualquer plano —, a cláusula de OBJETO e CONDIÇÕES COMERCIAIS
 * foi generalizada para referenciar o plano escolhido pelo usuário na
 * Plataforma em vez de fixar "Enterprise"/"R$199,00", e os blocos de
 * pagamento manual (dados bancários/PIX) e assinatura/testemunhas foram
 * removidos (o aceite digital com timestamp substitui a assinatura física).
 * Recomenda-se validação jurídica desta adaptação antes do lançamento.
 *
 * `AdhesionContractText` (out/2026): o texto sozinho, para a etapa de plano do
 * cadastro mostrá-lo de novo numa caixa rolável ao lado do aceite (pedido do
 * Bruno — o cliente não pode alegar desconhecimento). `compact` troca a altura
 * de tela cheia do modal pela da caixa.
 */
export const AdhesionContractText = ({
  compact = false,
}: {
  compact?: boolean;
}) => (
  <Content compact={compact}>
    <h3>Objeto</h3>
    <p>
      Por meio deste Termo de Adesão, o(a) Usuário(a) manifesta sua concordância
      em aderir ao plano por ele(a) selecionado na Plataforma (Free, Starter,
      Business ou Enterprise), disponibilizado por{' '}
      <b>ENERGY PASS CONSULTORIA E GESTAO DE ENERGIA LTDA</b>, inscrita no CNPJ
      49.076.630/0001-39, com sede na Rua Uirapuru, n° 615 – Letra B, Jardim São
      Gonçalo, Campinas/SP, e com endereço de e-mail{' '}
      <b>contato@energypass.com.br</b>, cujas funcionalidades, condições e
      valores aqui descritos, bem como nos Termos e Condições Gerais de Uso da
      plataforma e na Política de Privacidade e Proteção de Dados Pessoais, são
      aceitos em conjunto com o presente Termo de Adesão.
    </p>

    <h3>Descrição do plano contratado</h3>
    <table>
      <thead>
        <tr>
          <th>Plano</th>
          <th>Descrição</th>
          <th>Funcionalidades</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <b>Free</b>
          </td>
          <td>
            Pacote de entrada do EnergyPass para quem quer entender melhor a
            conta de luz de sua residência ou empresa. Este plano é gratuito,
            limitado a uma Unidade Consumidora por CPF/CNPJ.
          </td>
          <td>
            <ul>
              <li>Relatório detalhado da Conta de luz</li>
              <li>Acesso ao Dashboard</li>
              <li>Histórico de Consumo</li>
              <li>
                Validação do Faturamento e identificação de cobranças indevidas
              </li>
              <li>Cálculo da Economia com Geração Distribuída</li>
              <li>Upload manual da conta de luz e dos dados de geração</li>
              <li>Oportunidades de economia e contratação pela plataforma</li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>
            <b>Starter</b>
          </td>
          <td>
            Para residências ou empresas atendidas na baixa tensão, que querem
            ter maiores informações sobre seus gastos com energia elétrica e
            descobrir quanto podem economizar. Este plano tem um preço de R$
            19,90/mês e é válido apenas para clientes do grupo B (baixa tensão),
            e inclui 1 unidade consumidora. Caso queiram colocar outra UC, podem
            optar pela contratação de UCs adicionais, a R$ 19,90/mês.
          </td>
          <td>
            <ul>
              <li>Relatório detalhado da Conta de luz</li>
              <li>Acesso ao Dashboard</li>
              <li>Histórico de Consumo</li>
              <li>
                Validação do Faturamento e identificação de cobranças indevidas
              </li>
              <li>Validação da geração do sistema fotovoltaico</li>
              <li>Cálculo da Economia com Geração Distribuída</li>
              <li>Upload automático da conta de luz e dos dados de geração</li>
              <li>
                Notificações via e-mail e WhatsApp sobre a fatura de energia e
                status do sistema fotovoltaico
              </li>
              <li>Oportunidades de economia e contratação pela plataforma</li>
              <li>Contratação de serviços adicionais</li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>
            <b>Business</b>
          </td>
          <td>
            Para empresas atendidas em baixa ou média tensão, que querem ter
            maiores informações sobre seus gastos com energia elétrica,
            descobrir quanto podem economizar e gerenciar unidades consumidoras
            de filiais. Este plano tem um preço de R$ 49,90/mês, é válido para
            clientes grupo B (baixa tensão) ou grupo A (média ou alta tensão), e
            inclui 1 unidade consumidora na assinatura. Caso queiram incluir
            mais UCs, é possível por um custo adicional de R$ 49,90 para UCs do
            grupo A e R$ 19,90 para UCs do grupo B.
          </td>
          <td>
            <ul>
              <li>Relatório detalhado da Conta de luz</li>
              <li>Acesso ao Dashboard</li>
              <li>Histórico de Consumo</li>
              <li>
                Validação do Faturamento e identificação de cobranças indevidas
              </li>
              <li>Validação da geração do sistema fotovoltaico</li>
              <li>Cálculo da Economia com Geração Distribuída</li>
              <li>Upload automático da conta de luz e dos dados de geração</li>
              <li>
                Notificações via e-mail e WhatsApp sobre a fatura de energia e
                status do sistema fotovoltaico
              </li>
              <li>Oportunidades de economia e contratação pela plataforma</li>
              <li>Contratação de serviços adicionais</li>
              <li>
                Gestão e otimização do rateio de créditos de geração distribuída
              </li>
              <li>Análise e otimização tarifária</li>
              <li>Análise e otimização dos contratos de demanda</li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>
            <b>Enterprise</b>
          </td>
          <td>
            Para médias e grandes empresas, que possuem diversas unidades
            consumidoras para gerir e buscam ter maiores informações sobre seus
            gastos com energia elétrica e oportunidades de economia. Este plano
            tem um preço de R$ 199,90/mês, é válido para clientes grupo B (baixa
            tensão) ou grupo A (média ou alta tensão) e inclui 8 unidades
            consumidoras na assinatura. Caso queiram incluir mais UCs, é
            possível por um custo adicional de R$ 29,90 para UCs do grupo A e R$
            9,90 para UCs do grupo B.
          </td>
          <td>
            <ul>
              <li>Relatório detalhado da Conta de luz</li>
              <li>Acesso ao Dashboard</li>
              <li>Histórico de Consumo</li>
              <li>
                Validação do Faturamento e identificação de cobranças indevidas
              </li>
              <li>Validação da geração do sistema fotovoltaico</li>
              <li>Cálculo da Economia com Geração Distribuída</li>
              <li>Upload automático da conta de luz e dos dados de geração</li>
              <li>
                Notificações via e-mail e WhatsApp sobre a fatura de energia e
                status do sistema fotovoltaico
              </li>
              <li>Oportunidades de economia e contratação pela plataforma</li>
              <li>Contratação de serviços adicionais</li>
              <li>
                Gestão e otimização do rateio de créditos de geração distribuída
              </li>
              <li>Análise e otimização tarifária</li>
              <li>Análise e otimização dos contratos de demanda</li>
              <li>Indicadores e Benchmarks</li>
            </ul>
          </td>
        </tr>
      </tbody>
    </table>

    <h3>Serviços Adicionais</h3>
    <p>
      São serviços relativos à conta de luz, distribuidora de energia ou ao
      sistema fotovoltaico, que não estão inclusos na assinatura. Estes serviços
      incluem, mas não se limitam à:
    </p>
    <ul>
      <li>Solicitação de alteração tarifária junto à distribuidora</li>
      <li>
        Solicitação de alteração da demanda contratada junto à distribuidora
      </li>
      <li>
        Solicitação de alteração do rateio de créditos junto à distribuidora
      </li>
      <li>Abertura de reclamação e questionamentos junto à distribuidora</li>
    </ul>

    <h3>Aceite e vigência</h3>
    <p>
      O presente Termo de Adesão entra em vigor a partir da data de seu aceite
      eletrônico no momento do cadastro, permanecendo válido enquanto o plano
      estiver ativo e devidamente pago, conforme periodicidade escolhida.
    </p>

    <h3>Condições comerciais</h3>
    <p>
      O valor do plano escolhido pelo(a) Usuário(a) corresponde ao valor mensal
      vigente na tabela de planos da Plataforma no momento da contratação,
      incluindo eventuais unidades consumidoras adicionais conforme os limites e
      custos de cada plano. A forma de pagamento será informada pela Plataforma
      no momento da contratação ou pela equipe comercial, conforme o plano
      escolhido. Em caso de alteração futura nos valores ou condições, o(a)
      Usuário(a) será previamente informado(a) para que possa renovar, migrar ou
      cancelar seu plano.
    </p>
    <p>
      Todos os impostos, taxas e contribuições incidentes ou que venham a
      incidir sobre as operações decorrentes do presente Termo de Adesão serão
      suportados pela parte definida como contribuinte nos termos da legislação
      aplicável, não lhe assistindo o direito de qualquer reembolso pela outra
      parte, seja a que título for.
    </p>

    <h3>Integração com os Termos Gerais</h3>
    <p>
      Este Termo de Adesão complementa e integra os Termos e Condições Gerais de
      Uso e a Política de Privacidade e Proteção de Dados Pessoais da
      plataforma, prevalecendo estes, no que couber, sobre disposições
      conflitantes aqui contidas.
    </p>

    <h3>Cancelamento</h3>
    <p>
      O(a) Usuário(a) poderá solicitar o cancelamento do plano a qualquer
      momento, observado que este possui vigência anual, com pagamentos mensais.
      Em caso de cancelamento antes do término do período contratado, será
      devido o pagamento das parcelas vincendas até o final do ciclo anual,
      salvo disposição diversa prevista nos Termos e Condições Gerais de Uso,
      incluindo, mas não se limitando, a falhas ou omissões da contratada.
    </p>
    <p>
      Considera-se falhas ou omissões da contratada, para efeitos desta
      cláusula, os seguintes casos:
    </p>
    <ul>
      <li>
        Interrupções não programadas e prolongadas no serviço, sem comunicação
        adequada ou sem previsão de solução;
      </li>
      <li>
        Erros sistemáticos no cálculo ou na distribuição de energia que impactem
        negativamente os Usuários;
      </li>
      <li>
        Falhas na segurança da plataforma que comprometam a confidencialidade,
        integridade ou disponibilidade dos dados dos usuários;
      </li>
      <li>
        Não conformidade com as regulamentações aplicáveis ao setor de
        distribuição de energia, resultando em prejuízos ou riscos de
        penalidades para os Usuários;
      </li>
      <li>
        Erros ou omissões nas funcionalidades essenciais da plataforma que
        impeçam a utilização plena dos serviços acordados.
      </li>
    </ul>

    <h3>Foro</h3>
    <p>
      Fica eleito o foro da comarca de Campinas/SP, com renúncia a qualquer
      outro, por mais privilegiado que seja, para dirimir eventuais
      controvérsias decorrentes deste Termo de Adesão.
    </p>
  </Content>
);

export const ModalAdhesionContract = ({
  show,
  toggle,
}: ModalAdhesionContractProps) => (
  <Modal
    show={show}
    size="xl"
    title="Termo de Adesão à Plataforma EnergyPass"
    close={toggle}
  >
    <AdhesionContractText />
  </Modal>
);
